// ===========================================
// Class Links hooks (web links as class resources, 2026-09-07)
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  classLinksApi,
  ClassLinksQuery,
  CreateClassLinkInput,
  UpdateClassLinkInput,
} from '../api/classLinks.api';

export const classLinkKeys = {
  all: ['classLinks'] as const,
  list: (filters?: ClassLinksQuery) => [...classLinkKeys.all, 'list', filters ?? {}] as const,
};

type ApiError = Error & { response?: { data?: { message?: string } } };

/** Links for a class (cascading to its program level) or, with no filter, every link the role may see. */
export function useClassLinks(filters?: ClassLinksQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: classLinkKeys.list(filters),
    queryFn: () => classLinksApi.list(filters),
    staleTime: 60 * 1000,
    enabled: options?.enabled !== false,
  });
}

export function useCreateClassLink() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (data: CreateClassLinkInput) => classLinksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classLinkKeys.all });
      enqueueSnackbar('Link added', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to add link', { variant: 'error' });
    },
  });
}

export function useUpdateClassLink() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClassLinkInput }) => classLinksApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classLinkKeys.all });
      enqueueSnackbar('Link updated', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to update link', { variant: 'error' });
    },
  });
}

export function useDeleteClassLink() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => classLinksApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classLinkKeys.all });
      enqueueSnackbar('Link removed', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to remove link', { variant: 'error' });
    },
  });
}
